"use client";

import React, { useRef, useState } from "react";
import { Download } from "lucide-react";
import { useLanguage } from "@/lib/i18n/language-provider";

export type InteractiveButtonProps = {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  requirements: string[];
  onDownload: () => void;
};

export default function InteractiveButton({ title, subtitle, icon, requirements, onDownload }: InteractiveButtonProps) {
  const { t } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePosition({ x, y });
  };

  return (
    <div
      ref={buttonRef}
      className="relative group cursor-pointer transform transition-all duration-500 ease-out hover:scale-[1.02] hover:-translate-y-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setMousePosition({ x: 0, y: 0 }); }}
      onMouseMove={handleMouseMove}
      onClick={onDownload}
    >
      <div className="relative overflow-hidden bg-black rounded-xl border border-gray-800 p-8 shadow-2xl backdrop-blur-sm transition-all duration-500 hover:border-gray-600 hover:shadow-3xl">
        <div
          className={`absolute inset-0 opacity-0 transition-opacity duration-300 pointer-events-none ${isHovered ? 'opacity-100' : ''}`}
          style={{
            background: `radial-gradient(circle 150px at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 40%, transparent 70%)`,
          }}
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transform -skew-x-12 transition-all duration-1000 ease-out ${isHovered ? 'opacity-10 translate-x-full' : '-translate-x-full'}`}
          style={{ width: '30%' }}
        />

        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 bg-white rounded-full transition-all duration-1000 ${isHovered ? 'opacity-30 scale-100' : 'opacity-0 scale-0'}`}
              style={{
                left: `${20 + (i * 15)}%`,
                top: `${10 + (i * 12)}%`,
                transitionDelay: `${i * 100}ms`,
                animation: isHovered ? `float ${2 + i * 0.5}s ease-in-out infinite` : 'none'
              }}
            />
          ))}
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="text-white text-3xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
              {icon}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-1 transition-all duration-300 group-hover:text-gray-100">{title}</h3>
              <p className="text-gray-400 text-sm transition-all duration-300 group-hover:text-gray-300">{subtitle}</p>
            </div>
          </div>

          <div className="mb-8 space-y-2">
            {requirements.map((req, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 text-gray-300 text-sm transition-all duration-500 transform ${isHovered ? 'translate-x-1' : ''}`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <div className={`w-1.5 h-1.5 bg-white rounded-full transition-all duration-500 ${isHovered ? 'scale-125 shadow-sm shadow-white' : ''}`} />
                <span className="transition-colors duration-300 group-hover:text-gray-2 00">{req}</span>
              </div>
            ))}
          </div>

          <button className="w-full bg-white hover:bg-gray-100 text-black font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group/btn">
            <Download size={20} className="transition-transform duration-300 group-hover/btn:scale-110 group-hover/btn:-translate-y-0.5" />
            <span className="transition-all duration-300">{t("downloads.cta", { title })}</span>
          </button>
        </div>

        <div className={`absolute inset-0 rounded-xl transition-all duration-500 ${isHovered ? 'shadow-lg shadow-white/10 ring-1 ring-white/20' : ''}`} />
        <div className={`absolute inset-0 rounded-xl transition-all duration-700 ${isHovered ? 'bg-gradient-to-br from-white/5 via-transparent to-white/5' : ''}`} />
      </div>
    </div>
  );
} 